import React, { useState } from 'react';
import cx from 'classnames';
import { Table } from 'antd';
import pluralize from 'pluralize';
import { Formik } from 'formik';
import { Form } from 'formik-antd';
import { nanoid } from '@reduxjs/toolkit';
import { ColumnsType } from 'antd/es/table/interface';
import * as Yup from 'yup';

import BaseTableHeader from 'components/wrappers/BaseTableHeader';
import BasePageLabel from 'components/wrappers/BasePageLabel';
import SubmitButton from 'components/formik/SubmitButton';
import { pageDefaultSizeTable } from 'constants/index';
import { BaseUploadFile } from 'components/wrappers/BaseFileUpload';
import { getFullName } from 'utils/personUtils';
import {
  AssignmentBulkImportViewModel,
  WorkerTypeEnum,
} from 'api/adminApiClient';
import FormItem from 'components/formik/FormItem';
import WorkerUpload from 'components/modals/WorkerModal/WorkerUpload';
import { useAppSelector } from 'hooks/useAppSelector';
import { isAdminSelector } from 'redux/selectors/authenticationSelectors';
import { getWorkerType } from 'utils/workerUtils';
import {
  mapAssignmentBulkImportToInitialValues,
  mapAssignmentBulkImportToRequest,
} from 'utils/assignmentUtils';
import { AssignmentBulkImportModel } from '../AssignmentBulkImportPage';

import styles from './AssignmentBulkImportTableSection.module.less';

type Props = {
  onSubmit: (data: AssignmentBulkImportViewModel[]) => void;
  assignments: AssignmentBulkImportModel[];
};

const assignmentValidationSchema = Yup.object({
  workerFileKey: Yup.array().of(Yup.string().defined()),
});

const validationSchema = Yup.object({
  assignments: Yup.array().of(assignmentValidationSchema),
});

export interface AssignmentBulkImportFormModel {
  id?: string;
  workerFileKey: string[];
  memberId?: string;
  assignmentType?: string;
  contractorCompanyCompanyName?: string;
  contractorCompanyPrimaryContactFirstName?: string;
  contractorCompanyPrimaryContactLastName?: string;
  contractorCompanyEmail?: string;
  contractorCompanyPrimaryContactPhone?: string;
  contractorCompanyTaxId?: number;
  contractorCompanyCountry?: string;
  contractorCompanyState?: string;
  contractorCompanyCity?: string;
  contractorCompanyAddress?: string;
  contractorCompanyZip?: string;
  workerFirstName?: string;
  workerLastName?: string;
  workerEmail?: string;
  workerPhone?: string;
  workerType?: string;
  workerCountry?: string;
  workerState?: string;
  workerCity?: string;
  workerAddress?: string;
  workerZip?: string;
  workerTaxId?: number;
  customerKey?: string;
  primaryContactEmail?: string;
  billingContactEmail?: string;
  billingContact1FirstName?: string;
  billingContact1LastName?: string;
  billingContact1Email?: string;
  billingContact1Phone?: string;
  billingContact2FirstName?: string;
  billingContact2LastName?: string;
  billingContact2Email?: string;
  billingContact2Phone?: string;
  billingContact3FirstName?: string;
  billingContact3LastName?: string;
  billingContact3Email?: string;
  billingContact3Phone?: string;
  billingContact4FirstName?: string;
  billingContact4LastName?: string;
  billingContact4Email?: string;
  billingContact4Phone?: string;
  supervisorFirstName?: string;
  supervisorLastName?: string;
  supervisorEmail?: string;
  supervisorPhone?: string;
  supervisorTitle?: string;
  tsApprover1FirstName?: string;
  tsApprover1LastName?: string;
  tsApprover1Email?: string;
  tsApprover1Phone?: string;
  tsApprover2FirstName?: string;
  tsApprover2LastName?: string;
  tsApprover2Email?: string;
  tsApprover2Phone?: string;
  tsApprover3FirstName?: string;
  tsApprover3LastName?: string;
  tsApprover3Email?: string;
  tsApprover3Phone?: string;
  tsApprover4FirstName?: string;
  tsApprover4LastName?: string;
  tsApprover4Email?: string;
  tsApprover4Phone?: string;
  tsApprover5FirstName?: string;
  tsApprover5LastName?: string;
  tsApprover5Email?: string;
  tsApprover5Phone?: string;
  workCountry?: string;
  workState?: string;
  workCity?: string;
  workAddress?: string;
  workZip?: string;
  billingCountry?: string;
  billingState?: string;
  billingCity?: string;
  billingAddress?: string;
  billingZip?: string;
  billingEmail?: string;
  jobTitle?: string;
  employmentType?: string;
  startDate?: Date;
  endDate?: Date;
  jobDescription?: string;
  jobCategory?: string;
  anticipatedSchedule?: string;
  customerPurchaseOrderNumber?: string;
  workFromHome?: string;
  prescreeningRequired?: string;
  prescreeningBilledTo?: string;
  specialInstructions?: string;
  recruiter1Email?: string;
  recruiter1FullName?: string;
  recruiter1RateType?: string;
  recruiter1Rate?: number;
  recruiter2Email?: string;
  recruiter2FullName?: string;
  recruiter2RateType?: string;
  recruiter2Rate?: number;
  recruiter3Email?: string;
  recruiter3FullName?: string;
  recruiter3RateType?: string;
  recruiter3Rate?: number;
  recruiter4Email?: string;
  recruiter4FullName?: string;
  recruiter4RateType?: string;
  recruiter4Rate?: number;
  recruiter5Email?: string;
  recruiter5FullName?: string;
  recruiter5RateType?: string;
  recruiter5Rate?: number;
  payType?: string;
  payRate?: number;
  billRate?: number;
  overtime?: string;
  otBillRate?: number;
  otPayRate?: number;
  dtBillRate?: number;
  dtPayRate?: number;
  vmsMspPortion?: number;
  mbpPortion?: number;
  profitRate?: number;
  payRate2Type?: string;
  payRate2?: number;
  billRate2?: number;
  specialInstructions2?: string;
  payRate3Type?: string;
  payRate3?: number;
  billRate3?: number;
  specialInstructions3?: string;
  payRate4Type?: string;
  payRate4?: number;
  billRate4?: number;
  specialInstructions4?: string;
  payRate5Type?: string;
  payRate5?: number;
  billRate5?: number;
  specialInstructions5?: string;
  payRate6Type?: string;
  payRate6?: number;
  billRate6?: number;
  specialInstructions6?: string;
  payRate7Type?: string;
  payRate7?: number;
  billRate7?: number;
  specialInstructions7?: string;
  payRate8Type?: string;
  payRate8?: number;
  billRate8?: number;
  specialInstructions8?: string;
  payRate9Type?: string;
  payRate9?: number;
  billRate9?: number;
  specialInstructions9?: string;
  payRate10Type?: string;
  payRate10?: number;
  billRate10?: number;
  specialInstructions10?: string;
  payRate11Type?: string;
  payRate11?: number;
  billRate11?: number;
  specialInstructions11?: string;
  payRate12Type?: string;
  payRate12?: number;
  billRate12?: number;
  specialInstructions12?: string;
  payRate13Type?: string;
  payRate13?: number;
  billRate13?: number;
  specialInstructions13?: string;
  payRate14Type?: string;
  payRate14?: number;
  billRate14?: number;
  specialInstructions14?: string;
  payRate15Type?: string;
  payRate15?: number;
  billRate15?: number;
  specialInstructions15?: string;
  payRate16Type?: string;
  payRate16?: number;
  billRate16?: number;
  specialInstructions16?: string;
  payRate17Type?: string;
  payRate17?: number;
  billRate17?: number;
  specialInstructions17?: string;
  payRate18Type?: string;
  payRate18?: number;
  billRate18?: number;
  specialInstructions18?: string;
  payRate19Type?: string;
  payRate19?: number;
  billRate19?: number;
  specialInstructions19?: string;
  splitCompanyName?: string;
  splitRateType?: string;
  splitAmount?: number;
  splitSubcontractorRate?: number;
  splitCountry?: string;
  splitState?: string;
  splitCity?: string;
  splitAddress?: string;
  splitZip?: string;
  splitFirstName?: string;
  splitLastName?: string;
  splitEmail?: string;
  splitPhoneNumber?: string;
}

export interface AssignmentBulkImportFormData {
  assignments: AssignmentBulkImportFormModel[];
}

interface UploadedFileModel {
  file: BaseUploadFile[];
  index: number;
}

export default function AssignmentBulkImportTableSection(props: Props) {
  const { assignments, onSubmit } = props;
  const [listOfUploadedFiles, setListOfUploadedFiles] = useState<
    UploadedFileModel[]
  >([]);
  const [currentPage, setCurrentPage] = useState(1);
  const isAdmin = useAppSelector(isAdminSelector);
  const initialValues: AssignmentBulkImportFormData =
    mapAssignmentBulkImportToInitialValues(assignments);

  const handleFileChange = (files: BaseUploadFile[], index: number = 0) => {
    const hasFileInTheList = listOfUploadedFiles.find(
      (file) => file.index === index,
    );

    if (hasFileInTheList) {
      const newList = listOfUploadedFiles.map((file) =>
        file.index === index ? { index: index, file: files } : file,
      );
      return setListOfUploadedFiles(newList);
    }

    const newFile: UploadedFileModel = { index, file: files };
    const listOfUploadedFilesDuplicate = [...listOfUploadedFiles];
    listOfUploadedFilesDuplicate.push(newFile);

    setListOfUploadedFiles(listOfUploadedFilesDuplicate);
  };

  const columns: ColumnsType<AssignmentBulkImportFormModel> = [
    {
      title: 'Assignment type',
      key: 'assignmentType',
      render: (assignment: AssignmentBulkImportFormModel) =>
        assignment.assignmentType,
    },
    {
      title: 'Contractor company name/Parent SC name',
      key: 'companyName',
      render: (assignment: AssignmentBulkImportFormModel) =>
        assignment.contractorCompanyCompanyName,
    },
    {
      title: 'Worker',
      key: 'workerName',
      render: (assignment: AssignmentBulkImportFormModel) =>
        getFullName(assignment.workerFirstName, assignment.workerLastName),
    },
    {
      title: 'Customer key',
      key: 'customerKey',
      render: (assignment: AssignmentBulkImportFormModel) =>
        assignment.customerKey,
    },
    {
      title: 'Parent SC/Worker W9',
      key: 'action',
      render: (
        assignment: AssignmentBulkImportFormModel,
        record: AssignmentBulkImportFormModel,
        index: number,
      ) => {
        const correctIndex =
          currentPage === 1
            ? index
            : (currentPage - 1) * pageDefaultSizeTable + index;

        const defaultFile = listOfUploadedFiles.find(
          (file) => file.index === correctIndex,
        )?.file;

        return (
          <FormItem
            name={`assignments[${correctIndex}].workerFileKey`}
            showValidateError={false}
          >
            <div className={styles.upload}>
              <WorkerUpload
                isAdmin={isAdmin as boolean}
                isSimpleUpload={true}
                key={correctIndex}
                index={correctIndex}
                fieldName={`assignments[${correctIndex}].workerFileKey`}
                workerFiles={defaultFile}
                workerType={getWorkerType(assignment.workerType)}
                onChange={handleFileChange}
              />
            </div>
          </FormItem>
        );
      },
    },
  ];

  const assignmentsMessage = pluralize(
    'Assignment',
    assignments?.length || 0,
    false,
  );

  const getAllAssignmentsCountMessage = () => {
    const assignmentsLength = assignments?.length || 0;

    const msg = pluralize('Record', assignmentsLength, true);
    return msg + ' to be added';
  };

  const getAssignmentsCountMessage = () => {
    const assignmentsLength = assignments.filter(
      (assignment) =>
        getWorkerType(assignment?.workerType) !== WorkerTypeEnum.W2,
    ).length;

    return assignmentsLength + ' Parent SC/Worker W9 to be uploaded';
  };

  const renderSubTitle = () => (
    <>
      <div>{getAllAssignmentsCountMessage()}</div>
      <div>{getAssignmentsCountMessage()}</div>
    </>
  );

  const handleSubmit = async (data: AssignmentBulkImportFormData) => {
    const values = mapAssignmentBulkImportToRequest(
      assignments,
      data.assignments,
    );
    await onSubmit(values);
  };

  const handlePaginationChange = (page: number) => {
    setCurrentPage(page);
  };

  return (
    <>
      <BasePageLabel label="Upload agreements" />
      <div className="page-box page-inner">
        <div className="page-inner-container">
          <Formik
            enableReinitialize
            initialValues={initialValues}
            onSubmit={handleSubmit}
            validationSchema={validationSchema}
          >
            <Form>
              <BaseTableHeader
                title={assignmentsMessage}
                enableSubTitle={true}
                subTitle={renderSubTitle()}
              >
                <div>
                  <SubmitButton>Import</SubmitButton>
                </div>
              </BaseTableHeader>

              {initialValues.assignments.length > 0 && (
                <Table
                  className={cx(styles.table, 'custom-table')}
                  columns={columns}
                  rowKey={() => nanoid()}
                  dataSource={initialValues.assignments}
                  pagination={{
                    showSizeChanger: false,
                    total: initialValues.assignments.length || 0,
                    pageSize: pageDefaultSizeTable,
                    onChange: handlePaginationChange,
                  }}
                  scroll={{ x: '100%' }}
                />
              )}
            </Form>
          </Formik>
        </div>
      </div>
    </>
  );
}
